import { useCallback, useEffect, useState } from 'react';
import {
  type ExportFileEntity,
  type ExportFileType,
} from './ExportFileTypes.ts';

const FIELDS_KEY = 'Fields';
const SAVE_FLAG_KEY = 'SaveFields';

/**
 * Hook per la gestione del salvataggio in localStorage dei campi
 * selezionati per l'esportazione
 */
interface UseExportGeoJsonStorageDataOptions {
  type: ExportFileType;
  entity: ExportFileEntity;
  onSelect: (fields: string[]) => void;
  storageDiscriminator?: string;
}
export function useExportFileStorageData(
  options: UseExportGeoJsonStorageDataOptions,
) {
  const { type, entity, onSelect, storageDiscriminator } = options;
  const normalizedType = type === 'xlsx' ? 'csv' : type;
  const key = storageDiscriminator
    ? `${entity}_${storageDiscriminator}_${normalizedType}`
    : `${entity}_${normalizedType}`;

  // Flag di salvataggio
  const [saveFlag, setSaveFlag] = useState(false);

  useEffect(() => {
    // Recupero il flag di salvataggio dei campi
    const saveFieldData = localStorage.getItem(`${key}${SAVE_FLAG_KEY}`);

    // Se non è indicato il salvataggio dei campi, non caricare nessun default
    if (!saveFieldData) return;
    const saveFieldValue = JSON.parse(saveFieldData);
    if (!saveFieldValue) return;

    // Se è presente un flag, lo imposto nello stato
    setSaveFlag(saveFieldValue);

    // Recupero i campi selezionati
    const fieldData = localStorage.getItem(`${key}${FIELDS_KEY}`);
    if (fieldData) {
      const saved: string[] = JSON.parse(fieldData);
      onSelect(saved);
    }
  }, [key, onSelect]);

  /** Funzione per l'aggiornamento del localStorage, richiamata al salvataggio */
  const update = useCallback(
    (selectedFields: string[]) => {
      // Salvo il flag
      localStorage.setItem(`${key}${SAVE_FLAG_KEY}`, JSON.stringify(saveFlag));

      // Se il flag è attivo, salvo i campi selezionati
      if (saveFlag) {
        localStorage.setItem(
          `${key}${FIELDS_KEY}`,
          JSON.stringify(selectedFields),
        );
      }
    },
    [key, saveFlag],
  );

  return {
    saveFlag,
    setSaveFlag,
    update,
  };
}
